import React, { useEffect, useState } from 'react';

import { MoreOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  GetProp,
  MenuProps,
  Select,
  Table,
  TablePaginationConfig,
  TableProps,
  message,
} from 'antd';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Layout from '../../../components/Layout/Layout';
import { ApiErrorData } from '../../../models/ApiResponse';
import { ServerSideDataSchema } from '../../../models/ServerSideDataSchema';
import { TitaskIntegrationService } from '../../../services/TitaskIntegrationService';
import GeoVictoriaMigrationDetailModal from './components/GeoVictoriaMigrationDetailModal';
import NewGeoVictoriaMigrationModal from './components/NewGeoVictoriaMigrationModal';

export interface GeoVictoriaMigrationLogRowSchema {
  id: number;
  key: string;
  type: string;
  status: string;
  createdAt: Date;
  finishedAt: Date | null;
  params: any;
  payload: any;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

const PAGE_SIZE = 10;
const { RangePicker } = DatePicker;

function GeoVictoriaToBukMigration() {
  const { t } = useTranslation();
  const dateFormat: string = useSelector(
    (s: any) => s.intl?.dateFormat || 'DD/MM/YYYY'
  );
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState<
    GeoVictoriaMigrationLogRowSchema[]
  >([]);
  const [startDateFilter, setStartDateFilter] = useState(
    dayjs().add(-30, 'day').format('YYYY-MM-DD')
  );
  const [endDateFilter, setEndDateFilter] = useState(
    dayjs().format('YYYY-MM-DD')
  );
  const [statusFilter, setStatusFilter] = useState<
    'MANUAL' | 'AUTOMATIC' | undefined
  >();
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });
  const [newGeoVictoriaMigrationLoading, setNewGeoVictoriaMigrationLoading] =
    useState(false);
  const [
    isNewGeoVictoriaMigrationModalOpen,
    setIsNewGeoVictoriaMigrationModalOpen,
  ] = useState(false);
  const [
    isGeoVictoriaMigrationDetailModalOpen,
    setIsGeoVictoriaMigrationDetailModalOpen,
  ] = useState(false);
  const [selectedLog, setSelectedLog] = useState<
    GeoVictoriaMigrationLogRowSchema | undefined
  >();

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    fetchData();
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    startDateFilter,
    endDateFilter,
    statusFilter,
  ]);

  const init = async () => {
    setLoading(true);

    await fetchData();

    setLoading(false);
  };

  const fetchData = async (currentPage?: number) => {
    setLoading(true);

    const getGeoVictoriaMigrationTriggerLogResponse =
      await TitaskIntegrationService.getGeoVictoriaMigrationTriggerLog({
        page: currentPage || tableParams.pagination?.current || 1,
        pageSize: tableParams.pagination?.pageSize || PAGE_SIZE,
        startDate: startDateFilter,
        endDate: endDateFilter,
        type: statusFilter,
      });

    if (getGeoVictoriaMigrationTriggerLogResponse.success) {
      const data =
        getGeoVictoriaMigrationTriggerLogResponse.data as ServerSideDataSchema<GeoVictoriaMigrationLogRowSchema>;
      setDataSource(data.data);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: parseInt(`${data.totalRowsFiltered}`),
        },
      });
    } else {
      const error =
        getGeoVictoriaMigrationTriggerLogResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.geovictoria.geo_victoria_to_buk_migration.scene.errors.fetch_grid'
          ),
        3.5
      );
    }

    setLoading(false);
  };

  const handleTableChange: TableProps<GeoVictoriaMigrationLogRowSchema>['onChange'] =
    (pagination, filters, sorter) => {
      setTableParams({
        pagination,
        filters,
        ...sorter,
      });

      if (pagination.pageSize !== tableParams.pagination?.pageSize) {
        setDataSource([]);
      }
    };

  const onFinish = (values: any) => {
    setStartDateFilter(values.range[0].format('YYYY-MM-DD'));
    setEndDateFilter(values.range[1].format('YYYY-MM-DD'));

    if (values.type) {
      setStatusFilter(values.type);
    } else {
      setStatusFilter(undefined);
    }

    // setTableParams({
    //     ...tableParams,
    //     pagination: {
    //         ...tableParams.pagination,
    //         current: 1
    //     }
    // })

    console.log('finish', values);
  };

  const getDropdownMenu = (
    row: GeoVictoriaMigrationLogRowSchema
  ): MenuProps['items'] => {
    return [
      {
        key: '1',
        label: (
          <span
            onClick={() => {
              showGeoVictoriaMigrationDetail(row);
            }}
          >
            <SearchOutlined />
            {t(
              'scenes.geovictoria.geo_victoria_to_buk_migration.scene.dropdown.view_details'
            )}
          </span>
        ),
      },
    ];
  };

  const showNewGeoVictoriaMigrationModal = () => {
    if (!loading) {
      setIsNewGeoVictoriaMigrationModalOpen(true);
    }
  };

  const handleNewGeoVictoriaMigrationModalOk = async (
    startDate: string,
    endDate: string,
    frequency: 'diaria' | 'mensual'
  ) => {
    if (!newGeoVictoriaMigrationLoading) {
      setNewGeoVictoriaMigrationLoading(true);

      messageApi.open({
        key: 'submit-migration',
        type: 'loading',
        content: t(
          'scenes.geovictoria.geo_victoria_to_buk_migration.scene.messages.execute_migration_loading'
        ),
        duration: 0,
      });

      const executeMigrationResponse =
        await TitaskIntegrationService.executeGeoVictoriaToBukManualMigration({
          startDate,
          endDate,
          frequency,
        });

      if (executeMigrationResponse.success) {
        setNewGeoVictoriaMigrationLoading(false);
        messageApi.destroy();
        messageApi.success(
          t(
            'scenes.geovictoria.geo_victoria_to_buk_migration.scene.messages.execute_migration_success'
          ),
          3.5
        );
        setIsNewGeoVictoriaMigrationModalOpen(false);
      } else {
        const error = executeMigrationResponse.data as ApiErrorData;
        messageApi.destroy();
        messageApi.error(
          (error.message as string) ||
            t(
              'scenes.geovictoria.geo_victoria_to_buk_migration.scene.errors.execute_migration_failed'
            ),
          3.5
        );
        setLoading(false);
      }

      setNewGeoVictoriaMigrationLoading(false);
      await fetchData(1);
    }
  };

  const handleNewGeoVictoriaMigrationModalCancel = () => {
    if (!newGeoVictoriaMigrationLoading) {
      setIsNewGeoVictoriaMigrationModalOpen(false);
    }
  };

  const showGeoVictoriaMigrationDetail = (
    row: GeoVictoriaMigrationLogRowSchema
  ) => {
    setSelectedLog(row);
    setIsGeoVictoriaMigrationDetailModalOpen(true);
  };

  const handleGeoVictoriaMigrationDetailModalCancel = () => {
    setIsGeoVictoriaMigrationDetailModalOpen(false);
  };

  return (
    <>
      {contextHolder}

      <Layout title={''}>
        <Card>
          <Flex justify="space-between">
            <Form form={form} layout="inline" onFinish={onFinish}>
              <Form.Item
                name="range"
                label={t(
                  'scenes.geovictoria.geo_victoria_to_buk_migration.scene.filters.date_range'
                )}
              >
                <RangePicker
                  allowClear={false}
                  defaultValue={[dayjs().add(-30, 'day'), dayjs()]}
                  format={dateFormat}
                />
              </Form.Item>
              <Form.Item
                name="type"
                label={t(
                  'scenes.geovictoria.geo_victoria_to_buk_migration.scene.filters.type'
                )}
              >
                <Select
                  style={{ width: '125px' }}
                  placeholder={t(
                    'scenes.geovictoria.geo_victoria_to_buk_migration.scene.filters.select_all'
                  )}
                  allowClear
                  disabled={loading}
                >
                  <Select.Option key="MANUAL" value="MANUAL">
                    {t(
                      'scenes.geovictoria.geo_victoria_to_buk_migration.scene.filters.options.manual'
                    )}
                  </Select.Option>
                  <Select.Option key="AUTOMATIC" value="AUTOMATIC">
                    {t(
                      'scenes.geovictoria.geo_victoria_to_buk_migration.scene.filters.options.automatic'
                    )}
                  </Select.Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => (
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t(
                      'scenes.geovictoria.geo_victoria_to_buk_migration.scene.filters.filter'
                    )}
                  </Button>
                )}
              </Form.Item>
            </Form>

            <Button
              style={{ float: 'right' }}
              type="primary"
              loading={loading}
              onClick={() => {
                showNewGeoVictoriaMigrationModal();
              }}
            >
              <PlusOutlined />
              {t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.scene.buttons.manual_migration'
              )}
            </Button>
          </Flex>
        </Card>

        <Card style={{ marginTop: '12px' }}>
          <Table<GeoVictoriaMigrationLogRowSchema>
            dataSource={dataSource}
            bordered
            loading={loading}
            size="small"
            scroll={{ x: 1300 }}
            pagination={tableParams.pagination}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.columns.id'
              )}
              dataIndex="id"
              key="id"
            />
            <Column
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.columns.process_type'
              )}
              dataIndex="key"
              key="key"
              render={(key: string) => (
                <span>
                  {key === 'GEO_VICTORIA_MIGRATION_TO_BUK_PROCESS'
                    ? t(
                        'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.process_map.basic_migration'
                      )
                    : key}
                </span>
              )}
            />
            <Column
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.columns.mode'
              )}
              dataIndex="type"
              key="type"
              render={(type: string) => (
                <span>
                  {type === 'MANUAL'
                    ? t(
                        'scenes.geovictoria.geo_victoria_to_buk_migration.scene.filters.options.manual'
                      )
                    : t(
                        'scenes.geovictoria.geo_victoria_to_buk_migration.scene.filters.options.automatic'
                      )}
                </span>
              )}
            />
            <Column
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.columns.status'
              )}
              dataIndex="status"
              key="status"
              render={(status: string) => (
                <span>
                  {status === 'OK'
                    ? t(
                        'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.status.ok'
                      )
                    : status === 'PENDING'
                    ? t(
                        'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.status.pending'
                      )
                    : t(
                        'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.status.partial'
                      )}
                </span>
              )}
            />
            <Column
              width={160}
              align="center"
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.columns.created_at'
              )}
              dataIndex="createdAt"
              key="createdAtFormatted"
              render={(createdAt: Date) => (
                <span>{dayjs(createdAt).format(`${dateFormat} h:mm A`)}</span>
              )}
            />
            <Column
              width={160}
              align="center"
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.columns.finished_at'
              )}
              dataIndex="finishedAt"
              key="finishedAtFormatted"
              render={(finishedAt: Date) => (
                <span>
                  {finishedAt
                    ? dayjs(finishedAt).format(`${dateFormat} h:mm A`)
                    : t(
                        'scenes.geovictoria.geo_victoria_to_buk_migration.scene.table.status.not_finished'
                      )}
                </span>
              )}
            />
            <Column
              width={120}
              align="center"
              title=""
              key="actions"
              render={(row) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>

        {/* Modals */}
        <NewGeoVictoriaMigrationModal
          isNewGeoVictoriaMigrationModalOpen={
            isNewGeoVictoriaMigrationModalOpen
          }
          handleNewGeoVictoriaMigrationModalOk={
            handleNewGeoVictoriaMigrationModalOk
          }
          handleNewGeoVictoriaMigrationModalCancel={
            handleNewGeoVictoriaMigrationModalCancel
          }
          loading={newGeoVictoriaMigrationLoading}
        />

        {selectedLog && (
          <GeoVictoriaMigrationDetailModal
            selectedLog={selectedLog}
            isGeoVictoriaMigrationDetailModalOpen={
              isGeoVictoriaMigrationDetailModalOpen
            }
            handleGeoVictoriaMigrationDetailModalCancel={
              handleGeoVictoriaMigrationDetailModalCancel
            }
          />
        )}
      </Layout>
    </>
  );
}

export default GeoVictoriaToBukMigration;
